import { isSameDay } from 'date-fns';

import { DATE_PICKER_CELL_VARIANTS } from '../consts';
import { CellVariant, DatePickerDate } from '../types';

const isDateInRange = ({
  date,
  startDate,
  endDate,
}: {
  date: DatePickerDate;
  startDate: DatePickerDate;
  endDate: DatePickerDate;
}) => {
  if (!startDate || !endDate) {
    return false;
  }

  return (
    (startDate < date && date < endDate) || (endDate < date && date < startDate)
  );
};

const getDateVariantWithRange = ({
  dateToGetVariantFor,
  startDate,
  endDate,
  isOutside,
}: {
  dateToGetVariantFor: DatePickerDate;
  startDate: DatePickerDate;
  endDate: DatePickerDate;
  isOutside?: boolean;
}) => {
  if (
    isSameDay(startDate, dateToGetVariantFor) &&
    isSameDay(endDate, dateToGetVariantFor)
  ) {
    return isOutside
      ? DATE_PICKER_CELL_VARIANTS.OUTSIDE
      : DATE_PICKER_CELL_VARIANTS.DEFAULT;
  } else if (isSameDay(startDate, dateToGetVariantFor)) {
    return isOutside
      ? DATE_PICKER_CELL_VARIANTS.OUTSIDE_RANGE_START
      : DATE_PICKER_CELL_VARIANTS.RANGE_START;
  } else if (isSameDay(endDate, dateToGetVariantFor)) {
    return isOutside
      ? DATE_PICKER_CELL_VARIANTS.OUTSIDE_RANGE_END
      : DATE_PICKER_CELL_VARIANTS.RANGE_END;
  } else if (isDateInRange({ date: dateToGetVariantFor, startDate, endDate })) {
    return isOutside
      ? DATE_PICKER_CELL_VARIANTS.OUTSIDE_RANGE_MIDDLE
      : DATE_PICKER_CELL_VARIANTS.RANGE_MIDDLE;
  }

  return isOutside
    ? DATE_PICKER_CELL_VARIANTS.OUTSIDE
    : DATE_PICKER_CELL_VARIANTS.DEFAULT;
};

const getDateVariantWithStartSelected = ({
  dateToGetVariantFor,
  startDate,
  stateHoveredDate,
  isOutside,
}: {
  dateToGetVariantFor: DatePickerDate;
  startDate: DatePickerDate;
  stateHoveredDate?: DatePickerDate | null;
  isOutside?: boolean;
}) => {
  if (!stateHoveredDate) {
    return isOutside
      ? DATE_PICKER_CELL_VARIANTS.OUTSIDE
      : DATE_PICKER_CELL_VARIANTS.DEFAULT;
  }

  const isDateBeforeHoveredDate = dateToGetVariantFor < stateHoveredDate;
  const isDateAfterHoveredDate = dateToGetVariantFor > stateHoveredDate;

  if (
    isDateInRange({
      date: dateToGetVariantFor,
      startDate,
      endDate: stateHoveredDate,
    })
  ) {
    return isOutside
      ? DATE_PICKER_CELL_VARIANTS.OUTSIDE_RANGE_MIDDLE
      : DATE_PICKER_CELL_VARIANTS.RANGE_MIDDLE;
  }

  if (isSameDay(startDate, dateToGetVariantFor) && isDateAfterHoveredDate) {
    return isOutside
      ? DATE_PICKER_CELL_VARIANTS.OUTSIDE_RANGE_END
      : DATE_PICKER_CELL_VARIANTS.RANGE_END;
  } else if (
    isSameDay(startDate, dateToGetVariantFor) &&
    isDateBeforeHoveredDate
  ) {
    return isOutside
      ? DATE_PICKER_CELL_VARIANTS.OUTSIDE_RANGE_START
      : DATE_PICKER_CELL_VARIANTS.RANGE_START;
  }

  return isOutside
    ? DATE_PICKER_CELL_VARIANTS.OUTSIDE
    : DATE_PICKER_CELL_VARIANTS.DEFAULT;
};

export default function getDateVariant({
  range,
  selectedDates,
  date: dateToGetVariantFor,
  stateHoveredDate,
  isOutside,
  shouldShowSelection,
}: {
  range?: boolean;
  selectedDates: (DatePickerDate | null | undefined)[];
  date: DatePickerDate;
  stateHoveredDate?: DatePickerDate | null;
  isOutside?: boolean;
  shouldShowSelection?: boolean;
}): CellVariant {
  if (shouldShowSelection === false) {
    return DATE_PICKER_CELL_VARIANTS.DEFAULT;
  }
  if (!range) {
    return isOutside
      ? DATE_PICKER_CELL_VARIANTS.OUTSIDE
      : DATE_PICKER_CELL_VARIANTS.DEFAULT;
  }

  const [startDate, endDate] = selectedDates;

  if (startDate && endDate) {
    return getDateVariantWithRange({
      dateToGetVariantFor,
      startDate,
      endDate,
      isOutside,
    });
  }

  if (startDate && !endDate) {
    return getDateVariantWithStartSelected({
      dateToGetVariantFor,
      startDate,
      stateHoveredDate,
      isOutside,
    });
  }

  return isOutside
    ? DATE_PICKER_CELL_VARIANTS.OUTSIDE
    : DATE_PICKER_CELL_VARIANTS.DEFAULT;
}
